import React, { useState, useEffect, useRef } from 'react';

// Liste simplifiée des aéroports majeurs (Tu pourras en ajouter d'autres)
const MAJOR_AIRPORTS = [
  { code: 'YUL', city: 'Montréal', name: 'Pierre-Elliott-Trudeau', country: 'Canada' },
  { code: 'YQB', city: 'Québec', name: 'Jean Lesage', country: 'Canada' },
  { code: 'CDG', city: 'Paris', name: 'Charles de Gaulle', country: 'France' },
  { code: 'ORY', city: 'Paris', name: 'Orly', country: 'France' },
  { code: 'YYZ', city: 'Toronto', name: 'Pearson', country: 'Canada' },
  { code: 'YVR', city: 'Vancouver', name: 'International', country: 'Canada' },
  { code: 'YYC', city: 'Calgary', name: 'International', country: 'Canada' },
  { code: 'YHZ', city: 'Halifax', name: 'Stanfield', country: 'Canada' },
  { code: 'JFK', city: 'New York', name: 'JFK', country: 'USA' },
  { code: 'EWR', city: 'New York', name: 'Newark', country: 'USA' },
  { code: 'LHR', city: 'Londres', name: 'Heathrow', country: 'UK' },
  { code: 'DXB', city: 'Dubaï', name: 'International', country: 'UAE' },
  { code: 'CMN', city: 'Casablanca', name: 'Mohammed V', country: 'Maroc' },
  { code: 'ABJ', city: 'Abidjan', name: 'Félix-Houphouët-Boigny', country: 'Côte d\'Ivoire' },
  { code: 'TUN', city: 'Tunis', name: 'Carthage', country: 'Tunisie' },
  { code: 'DSS', city: 'Dakar', name: 'Blaise Diagne', country: 'Sénégal' }
];

interface Props {
  label: string;
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  icon?: string;
}

export default function AirportInput({ label, value, onChange, placeholder, icon }: Props) {
  const [query, setQuery] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Filtrer les aéroports
  const suggestions = MAJOR_AIRPORTS.filter(airport => 
    airport.city.toLowerCase().includes(query.toLowerCase()) || 
    airport.code.toLowerCase().includes(query.toLowerCase()) ||
    airport.country.toLowerCase().includes(query.toLowerCase())
  );

  // Fermer si on clique ailleurs
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const handleSelect = (code: string, city: string) => {
    setQuery(`${city} (${code})`); // Ce qu'on voit dans le champ
    onChange(code); // Ce qu'on envoie au backend (Juste le code YUL)
    setShowSuggestions(false);
  };

  return (
    <div className="flex-1 w-full relative" ref={wrapperRef}>
      <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">{label}</label>
      <div className="relative">
          <span className="absolute left-3 top-3.5 text-gray-400">{icon}</span>
          <input 
            type="text" 
            value={query}
            onChange={(e) => {
                setQuery(e.target.value);
                setShowSuggestions(true);
                // Si l'utilisateur efface tout, on vide le code parent
                if(e.target.value === '') onChange(''); 
            }}
            onFocus={() => setShowSuggestions(true)}
            className="w-full pl-9 p-3 border border-gray-300 rounded-lg font-bold text-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none bg-gray-50 transition"
            placeholder={placeholder} 
          />
      </div>

      {/* Menu Déroulant des suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
            {suggestions.map((airport) => (
                <div 
                    key={airport.code}
                    onClick={() => handleSelect(airport.code, airport.city)}
                    className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0 flex justify-between items-center group"
                >
                    <div>
                        <div className="font-bold text-gray-800">{airport.city}</div>
                        <div className="text-xs text-gray-500">{airport.name}, {airport.country}</div>
                    </div>
                    <div className="font-mono font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded text-xs group-hover:bg-blue-600 group-hover:text-white transition">
                        {airport.code}
                    </div>
                </div>
            ))}
        </div>
      )}
    </div>
  );
}
